import React, { useMemo } from 'react'
import './BookChart.css'
import {
  ROMANS_BOOK_DATA,
  BookChartData,
  BookArc,
  ArcRelationship,
  REL_SYMBOL,
  REL_NAME,
  REL_COLOR,
} from '../data/bookChart'

// ── Helpers ───────────────────────────────────────────────────────────────────

interface ChapterPos {
  startPct: number   // 0–100, top of this chapter card as % of total column
  heightPct: number  // 0–100, height of this chapter card as % of total column
}

function buildPositions(chapters: BookChartData['chapters'], total: number): ChapterPos[] {
  let cum = 0
  return chapters.map(ch => {
    const startPct  = (cum / total) * 100
    const heightPct = (ch.verseCount / total) * 100
    cum += ch.verseCount
    return { startPct, heightPct }
  })
}

interface ArcCoord {
  fromY: number   // 0–100 SVG units
  toY:   number
  midY:  number
}

function buildArcCoords(
  arcs: BookArc[],
  positions: ChapterPos[],
  chapters: BookChartData['chapters'],
): ArcCoord[] {
  return arcs.map(arc => {
    const fromPos  = positions[arc.fromChapter - 1]
    const toPos    = positions[arc.toChapter   - 1]
    const fromChVc = chapters[arc.fromChapter  - 1].verseCount
    const toChVc   = chapters[arc.toChapter    - 1].verseCount

    const fromY = fromPos.startPct + (arc.fromVerse / fromChVc) * fromPos.heightPct
    const toY   = toPos.startPct   + (arc.toVerse   / toChVc)   * toPos.heightPct
    return { fromY, toY, midY: (fromY + toY) / 2 }
  })
}

// ── Component ─────────────────────────────────────────────────────────────────

const BookChart: React.FC<{ data?: BookChartData }> = ({ data = ROMANS_BOOK_DATA }) => {
  const d = data

  const totalVerses = useMemo(
    () => d.chapters.reduce((s, c) => s + c.verseCount, 0),
    [d.chapters],
  )

  const positions  = useMemo(() => buildPositions(d.chapters, totalVerses), [d.chapters, totalVerses])
  const arcCoords  = useMemo(() => buildArcCoords(d.arcs, positions, d.chapters), [d.arcs, positions, d.chapters])

  // Separate arcs into primary and secondary for layering
  const primaryArcs   = d.arcs.filter(a => a.accent !== 'secondary')
  const secondaryArcs = d.arcs.filter(a => a.accent === 'secondary')

  // Group arcs by relationship for the sidebar legend (only types that appear)
  const usedRelationships = useMemo(() => {
    const seen = new Set<ArcRelationship>()
    d.arcs.forEach(a => seen.add(a.relationship))
    return Array.from(seen)
  }, [d.arcs])

  return (
    <div className="bc-wrap">
      <div className="bc-page">

        {/* ── Header ────────────────────────────────────────────────────────── */}
        <header className="bc-header">
          <div className="bc-header-left">
            <div className="bc-kicker">Book Chart · {d.classification}</div>
            <h1 className="bc-book-title">{d.book}</h1>
          </div>
          <div className="bc-header-right">
            {d.totalChapters} chapters · {totalVerses} verses · {d.arcs.length} arcs
          </div>
        </header>

        {/* ── Meta Banner ───────────────────────────────────────────────────── */}
        <div className="bc-meta-banner">
          <div className="bc-meta-field">
            <div className="bc-meta-key">Genre</div>
            <div className="bc-meta-val">{d.classification}</div>
          </div>
          <div className="bc-meta-field">
            <div className="bc-meta-key">Unity / Main Thesis</div>
            <div className="bc-meta-val">{d.unity}</div>
          </div>
          <div className="bc-meta-field">
            <div className="bc-meta-key">Occasion / Purpose</div>
            <div className="bc-meta-val">{d.problem}</div>
          </div>
        </div>

        {/* ── Body ──────────────────────────────────────────────────────────── */}
        <div className="bc-body">

          {/* ── Arc SVG Panel ─────────────────────────────────────────────── */}
          <div className="bc-arc-panel">
            {/*
              viewBox="0 0 60 100" + preserveAspectRatio="none"
              maps y: 0–100 directly to the chapter column height (0%–100%).
              x: 0 is leftmost (curve apex), 58 is right edge (attachment rail).
            */}
            <svg
              className="bc-arc-svg"
              viewBox="0 0 60 100"
              preserveAspectRatio="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Render secondary arcs first (behind primary) */}
              {d.arcs.map((arc, i) => {
                const { fromY, toY, midY } = arcCoords[i]
                const isPrimary  = arc.accent !== 'secondary'
                const curveX     = isPrimary ? 8 : 24   // primary curves further left
                const color      = REL_COLOR[arc.relationship]
                const sym        = REL_SYMBOL[arc.relationship]
                const strokeW    = isPrimary ? 1.8 : 1.2

                // Cubic bezier: start and end at right rail (x=56), apex at curveX
                const path = `M 56 ${fromY} C ${curveX} ${fromY} ${curveX} ${toY} 56 ${toY}`

                return (
                  <g key={arc.id} opacity={isPrimary ? 1 : 0.7}>
                    {/* Arc path */}
                    <path
                      d={path}
                      stroke={color}
                      strokeWidth={strokeW}
                      fill="none"
                      strokeLinecap="round"
                    />
                    {/* Endpoint dots */}
                    <circle cx="56" cy={fromY} r="1.2" fill={color} />
                    <circle cx="56" cy={toY}   r="1.2" fill={color} />
                    {/* Relationship symbol badge */}
                    <text
                      x={curveX - 1.5}
                      y={midY}
                      fontSize="5"
                      fontFamily="'Architects Daughter', cursive"
                      fill={color}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      {sym}
                    </text>
                  </g>
                )
              })}
            </svg>
          </div>

          {/* ── Chapter Cards Column ──────────────────────────────────────── */}
          <div className="bc-chapters">
            {d.chapters.map((ch, i) => (
              <div
                key={ch.number}
                className="bc-ch-card"
                style={{ flexGrow: ch.verseCount }}
              >
                <div className="bc-ch-head">
                  <span className="bc-ch-num">{ch.number}</span>
                  <span className="bc-ch-title">{ch.title}</span>
                  <span className="bc-ch-vc">{ch.verseCount} vv.</span>
                </div>
                <div className="bc-ch-theme">{ch.theme}</div>
                {ch.keyVerse && (
                  <span className="bc-ch-keyref">★ v.{ch.keyVerse}</span>
                )}
              </div>
            ))}
          </div>

          {/* ── Sidebar ───────────────────────────────────────────────────── */}
          <aside className="bc-sidebar">

            {/* Relationship Legend */}
            <div className="bc-side-card">
              <div className="bc-side-h">Arc Relationships</div>
              <div className="bc-legend-rows">
                {usedRelationships.map(rel => (
                  <div key={rel} className="bc-legend-row">
                    <span
                      className="bc-legend-sym"
                      style={{ color: REL_COLOR[rel] }}
                    >
                      {REL_SYMBOL[rel]}
                    </span>
                    <span className="bc-legend-name">{REL_NAME[rel]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Argument Flow List */}
            <div className="bc-side-card bc-flow-card">
              <div className="bc-side-h">Argument Flow</div>
              <div className="bc-flow-rows">
                {d.arcs.filter(a => a.accent !== 'secondary').map(arc => (
                  <div key={arc.id} className="bc-flow-item">
                    <span
                      className="bc-flow-sym"
                      style={{ color: REL_COLOR[arc.relationship] }}
                    >
                      {REL_SYMBOL[arc.relationship]}
                    </span>
                    <div className="bc-flow-body">
                      <span className="bc-flow-ref">
                        {d.book} {arc.fromChapter}:{arc.fromVerse}
                        {' '}→{' '}
                        {arc.toChapter}:{arc.toVerse}
                      </span>
                      {arc.label && (
                        <span className="bc-flow-label">{arc.label}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Adler method note */}
            <div className="bc-side-card bc-method-card">
              <div className="bc-side-h">Method</div>
              <p className="bc-method-text">
                Arcs map the author's argument. Each arc shows how one proposition
                supports, qualifies, or advances another — tracing the single main
                point across the book. (Fuller / Piper arcing method)
              </p>
            </div>

          </aside>
        </div>

      </div>
    </div>
  )
}

export default BookChart
